import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PathLink from "../../shared/seo/PathLink";
import { urls, copy, currentLang } from "../../shared/seo/links";
import {
  formatPhone,
  lawyerAddress,
  lawyerGovernorate,
  lawyerState,
  takesOnlineBooking,
} from "./lawyerHelpers";
import "./lawyers.css";

function Row({ icon, label, children }) {
  return (
    <li>
      <span className="avc-contact__icon">
        <i className={icon} aria-hidden="true"></i>
      </span>
      <span className="avc-contact__text">
        <span className="avc-contact__label">{label}</span>
        {children}
      </span>
    </li>
  );
}

// Contact and registration facts. No e-mail is shown: the directory does not
// publish it, and ONAT lawyers never gave one to us.
export default function LawyerContactCard({ lawyer }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = currentLang();
  const phone = formatPhone(lawyer.phoneNumber);
  const fax = formatPhone(lawyer.fax);
  const isOnat = lawyer.source === "onat";
  const governorate = lawyerGovernorate(lawyer);
  const level = urls.findBarLevel(lawyer.barLevel);
  const text = copy.lawyer(lang, lawyer);
  const sworn = lawyer.swornInOn ? new Date(lawyer.swornInOn).getFullYear() : null;
  const section = lawyer.barSection ? (lang === "ar" ? lawyer.barSection.ar : lawyer.barSection.fr) || lawyer.barSection.ar : null;
  const address = lawyerAddress(lawyer, lang);

  return (
    <div className="avc-sidebar-card avc-contact">
      <h2 className="avc-sidebar-card__title">{t("Lawyer_Contact_information")}</h2>
      <ul className="avc-contact__list">
        <Row icon="fas fa-phone-alt" label={t("Phone_number")}>
          {phone ? (
            <a href={phone.href} dir="ltr">
              {phone.display}
            </a>
          ) : (
            <span>{t("Not_Available")}</span>
          )}
        </Row>
        {fax && (
          <Row icon="fas fa-fax" label={t("profile_fax")}>
            <span dir="ltr">{fax.display}</span>
          </Row>
        )}
        {address && (
          <Row icon="fas fa-building" label={t("profile_office_address")}>
            <span>{address}</span>
          </Row>
        )}
        {governorate && (
          <Row icon="fas fa-map-marker-alt" label={t("profile_city")}>
            <PathLink to={urls.lawyersPath(lang, { governorate })}>
              {lawyerState(lawyer, lang)}
            </PathLink>
          </Row>
        )}
        {level && (
          <Row icon="fas fa-gavel" label={t("profile_bar_level")}>
            {governorate ? (
              <PathLink to={urls.lawyersPath(lang, { governorate, barLevel: level })}>
                {text.levelLabel}
              </PathLink>
            ) : (
              <span>{text.levelLabel}</span>
            )}
          </Row>
        )}
        {sworn && (
          <Row icon="far fa-calendar-alt" label={t("profile_sworn_in")}>
            <span>{sworn}</span>
          </Row>
        )}
        {section && (
          <Row icon="fas fa-landmark" label={t("profile_bar_section")}>
            <span>{section}</span>
          </Row>
        )}
      </ul>
      {takesOnlineBooking(lawyer) ? (
        <button
          type="button"
          className="avc-btn avc-btn--gold avc-btn--block"
          onClick={() => navigate("/book-appointment/" + lawyer.id)}
        >
          <i className="far fa-calendar-check" aria-hidden="true"></i>
          {t("Book_Appointment")}
        </button>
      ) : (
        <>
          {phone && (
            <a href={phone.href} className="avc-btn avc-btn--gold avc-btn--block">
              <i className="fas fa-phone-alt" aria-hidden="true"></i>
              {t("profile_call")}
            </a>
          )}
          {!isOnat && <p className="avc-contact__note">{t("profile_no_online_booking")}</p>}
        </>
      )}
      {isOnat && (
        <PathLink to={urls.sectionPath("contact", lang)} className="avc-contact__claim">
          {t("profile_claim")}
        </PathLink>
      )}
    </div>
  );
}
